import { refreshSpreadsheetCharts } from './spreadsheetCharts';

/**
 * Small spreadsheet formula engine for the in-app spreadsheet editor.
 *
 * Cells store `{ v, f }` where `f` is the raw formula source ("=SUM(A1:A5)")
 * and `v` is the cached computed value. Supported:
 *   - numbers, cell refs (B2), ranges inside functions (A1:A20)
 *   - + - * / ^ & and parentheses, unary minus, percent literals (12%)
 *   - SUM, AVERAGE/AVG, MIN, MAX, COUNT, COUNTA, ROUND, ABS, SQRT, IF, IFERROR
 *   - SUMIF/SUMIFS and COUNTIF/COUNTIFS with text, numeric and wildcard criteria
 *   - SUMPRODUCT array arithmetic/comparisons and LEFT for grouped summaries
 *   - AND, OR, NOT, ISNUMBER and INT for validation rules
 *
 * References may be same-sheet (B2) or sheet-qualified (Data!B2 and
 * 'Quarter 1'!B2). Errors evaluate to '#ERR', '#DIV/0', '#REF', '#VALUE',
 * or '#CYCLE' strings which the grid displays as-is.
 */

const FUNCTIONS = new Set(['SUM', 'AVERAGE', 'AVG', 'MIN', 'MAX', 'COUNT', 'COUNTA', 'ROUND', 'ABS', 'SQRT', 'IF', 'IFERROR', 'SUMIF', 'SUMIFS', 'COUNTIF', 'COUNTIFS', 'SUMPRODUCT', 'LEFT', 'AND', 'OR', 'NOT', 'ISNUMBER', 'INT']);

export function columnLabel(index) {
  let label = '';
  let n = index;
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/** "B12" → { r: 11, c: 1 } (0-based) or null. */
export function parseCellRef(ref) {
  const match = /^([A-Z]{1,3})([0-9]{1,4})$/i.exec(String(ref || '').trim());
  if (!match) return null;
  // Rows are 1-based, so "A0" is not a valid reference. Without this it became
  // r: -1, which resolved to an empty cell and made "=A0*100" quietly yield 0
  // instead of #REF.
  const row = parseInt(match[2], 10);
  if (row < 1) return null;
  const letters = match[1].toUpperCase();
  let c = 0;
  for (let i = 0; i < letters.length; i += 1) {
    c = c * 26 + (letters.charCodeAt(i) - 64);
  }
  return { r: row - 1, c: c - 1 };
}

export function isFormulaError(value) {
  return typeof value === 'string' && value.startsWith('#');
}

// ── Autofill helpers ─────────────────────────────────────────────────────────

/**
 * Shift the A1-style references (and range endpoints, which are just two
 * refs around a ':') in a formula by `rowDelta`/`colDelta`, honoring `$`
 * absolute markers: $A$1 never moves, A$1 keeps its row, $A1 keeps its
 * column. Function names and quoted strings are left untouched. A reference
 * pushed off the sheet (row < 1 or column < A) becomes '#REF'.
 *
 * Accepts formulas with or without the leading '='.
 */
export function shiftFormulaReferences(formula, rowDelta, colDelta) {
  const src = String(formula ?? '');
  const shiftSegment = segment => segment.replace(
    /(\$?)([A-Za-z]{1,3})(\$?)(\d{1,7})/g,
    (match, colAbs, letters, rowAbs, digits, offset, whole) => {
      const before = whole[offset - 1];
      const after = whole[offset + match.length];
      // Part of a longer identifier (e.g. "LOG10" inside a word) or a
      // function call ("SUM(") — not a cell reference.
      if (before && /[A-Za-z0-9_.]/.test(before)) return match;
      if (after && /[A-Za-z0-9_(!\[.]/.test(after)) return match;
      if (/^\s*\(/.test(whole.slice(offset + match.length))) return match;

      let col = 0;
      const upper = letters.toUpperCase();
      for (let i = 0; i < upper.length; i += 1) {
        col = col * 26 + (upper.charCodeAt(i) - 64);
      }
      col -= 1;
      let row = parseInt(digits, 10) - 1;
      if (row < 0) return match; // "A0" is not a real reference

      if (!colAbs) col += colDelta;
      if (!rowAbs) row += rowDelta;
      if (col < 0 || row < 0) return '#REF';
      return `${colAbs}${columnLabel(col)}${rowAbs}${row + 1}`;
    },
  );

  // Never rewrite quoted text, sheet names, structured column names, or
  // external workbook filenames. Quoted sheet references after ! still shift.
  let out = '';
  let plainStart = 0;
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === '[') {
      out += shiftSegment(src.slice(plainStart, i + 1));
      let depth = 1; let j = i + 1;
      while (j < src.length && depth) {
        if (src[j] === '[') depth++;
        if (src[j] === ']') depth--;
        j++;
      }
      out += src.slice(i + 1, j); i = j; plainStart = i;
    } else if (ch === '"' || ch === "'") {
      out += shiftSegment(src.slice(plainStart, i));
      let j = i + 1;
      while (j < src.length) {
        if (src[j] === ch) {
          if (src[j + 1] === ch) { j += 2; continue; }
          break;
        }
        j += 1;
      }
      out += src.slice(i, Math.min(j + 1, src.length));
      i = j + 1;
      plainStart = i;
    } else {
      i += 1;
    }
  }
  out += shiftSegment(src.slice(plainStart));
  return out;
}

/**
 * Autofill series detection: if `values` are all finite numbers forming a
 * constant-step arithmetic series, return the step (0 for a single value,
 * so extending it repeats the number). Otherwise return null.
 */
export function detectNumericSeriesStep(values) {
  if (!Array.isArray(values) || !values.length) return null;
  if (values.some(value => typeof value !== 'number' || !Number.isFinite(value))) return null;
  if (values.length === 1) return 0;
  const step = values[1] - values[0];
  for (let i = 2; i < values.length; i += 1) {
    if (Math.abs((values[i] - values[i - 1]) - step) > 1e-9) return null;
  }
  return step;
}

class FormulaError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

// ── Tokenizer ────────────────────────────────────────────────────────────────

function tokenize(src) {
  const tokens = [];
  let i = 0;
  const s = String(src);
  while (i < s.length) {
    const ch = s[i];
    if (ch === ' ' || ch === '\t') { i += 1; continue; }
    if (ch === '#') {
      const error = /^#(?:REF|DIV\/0|VALUE|N\/A|NAME\?|NUM|NULL|ERR|CYCLE)!?/i.exec(s.slice(i));
      if (!error) throw new FormulaError('#ERR');
      tokens.push({ type: 'error', value: error[0].replace(/!$/, '').toUpperCase() });
      i += error[0].length; continue;
    }
    if (ch === '"') {
      const match = /^"((?:[^"]|"")*)"/.exec(s.slice(i));
      if (!match) throw new FormulaError('#ERR');
      tokens.push({ type: 'string', value: match[1].replace(/""/g, '"') });
      i += match[0].length;
      continue;
    }
    if ('+-*/^&(),:'.includes(ch)) {
      tokens.push({ type: ch });
      i += 1;
      continue;
    }
    if ('=<>'.includes(ch)) {
      const match = /^(<=|>=|<>|=|<|>)/.exec(s.slice(i));
      tokens.push({ type: 'comparison', op: match[0] });
      i += match[0].length;
      continue;
    }
    if (ch === "'") {
      const match = /^'((?:[^']|'')+)'!\$?([A-Z]{1,3})\$?([0-9]{1,4})/i.exec(s.slice(i));
      if (!match) throw new FormulaError('#REF');
      const ref = parseCellRef(`${match[2]}${match[3]}`);
      if (!ref) throw new FormulaError('#REF');
      tokens.push({ type: 'ref', ref: { ...ref, sheet: match[1].replace(/''/g, "'") } });
      i += match[0].length;
      continue;
    }
    if (ch === '$' || /[A-Za-z_]/.test(ch)) {
      const sheetMatch = /^([A-Za-z_][A-Za-z0-9_.]*)!\$?([A-Z]{1,3})\$?([0-9]{1,4})/i.exec(s.slice(i));
      if (sheetMatch) {
        const ref = parseCellRef(`${sheetMatch[2]}${sheetMatch[3]}`);
        if (!ref) throw new FormulaError('#REF');
        tokens.push({ type: 'ref', ref: { ...ref, sheet: sheetMatch[1] } });
        i += sheetMatch[0].length;
        continue;
      }
      const absoluteRef = /^\$?([A-Z]{1,3})\$?([0-9]{1,4})/i.exec(s.slice(i));
      if (absoluteRef) {
        const ref = parseCellRef(`${absoluteRef[1]}${absoluteRef[2]}`);
        if (!ref) throw new FormulaError('#REF');
        tokens.push({ type: 'ref', ref });
        i += absoluteRef[0].length;
        continue;
      }
    }
    if (/[0-9.]/.test(ch)) {
      const match = /^[0-9]*\.?[0-9]+/.exec(s.slice(i));
      let value = parseFloat(match[0]);
      i += match[0].length;
      if (s[i] === '%') { value /= 100; i += 1; }
      tokens.push({ type: 'number', value });
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(s.slice(i));
      const word = match[0];
      i += word.length;
      if (s[i] === '(' && FUNCTIONS.has(word.toUpperCase())) {
        tokens.push({ type: 'function', name: word.toUpperCase() });
      } else if (/^(TRUE|FALSE)$/i.test(word)) {
        tokens.push({ type: 'boolean', value: word.toUpperCase() === 'TRUE' });
      } else {
        const ref = parseCellRef(word);
        if (!ref) throw new FormulaError('#REF');
        tokens.push({ type: 'ref', ref });
      }
      continue;
    }
    throw new FormulaError('#ERR');
  }
  return tokens;
}

// ── Parser / evaluator (recursive descent over the token list) ──────────────

function toNumber(value) {
  if (value === '' || value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (isFormulaError(value)) throw new FormulaError(value);
  const numeric = Number(String(value).replace(/,/g, ''));
  if (Number.isFinite(numeric)) return numeric;
  throw new FormulaError('#VALUE');
}

function matchesCriteria(value, criteria) {
  if (isFormulaError(value)) return false;
  const match = /^(<=|>=|<>|=|<|>)?(.*)$/.exec(String(criteria ?? ''));
  const op = match[1] || '=';
  const expected = match[2];
  const actual = String(value ?? '');
  if ((op === '=' || op === '<>') && /[?*~]/.test(expected)) {
    let pattern = '';
    const escape = c => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    for (let i = 0; i < expected.length; i += 1) {
      const c = expected[i];
      if (c === '~' && i + 1 < expected.length) pattern += escape(expected[++i]);
      else pattern += c === '*' ? '.*' : c === '?' ? '.' : escape(c);
    }
    const equal = new RegExp(`^${pattern}$`, 'i').test(actual);
    return op === '<>' ? !equal : equal;
  }
  const numeric = expected.trim() !== '' && Number.isFinite(Number(expected));
  if (numeric && !Number.isFinite(Number(actual))) return op === '<>';
  const a = numeric && Number.isFinite(Number(actual)) ? Number(actual) : actual.toLowerCase();
  const b = typeof a === 'number' ? Number(expected) : expected.toLowerCase();
  switch (op) {
    case '<>': return a !== b;
    case '<': return typeof a === typeof b && a < b;
    case '>': return typeof a === typeof b && a > b;
    case '<=': return typeof a === typeof b && a <= b;
    case '>=': return typeof a === typeof b && a >= b;
    default: return a === b;
  }
}

// Keep range dimensions so a 2×2 range cannot silently pair with a 4×1 range.
const isRange = value => value != null && Array.isArray(value.values);
const flatten = value => isRange(value) ? value.values : [value];
function combineValues(left, right, operation) {
  if (!isRange(left) && !isRange(right)) return operation(left, right);
  if (isRange(left) && isRange(right) &&
      (left.rows !== right.rows || left.cols !== right.cols)) throw new FormulaError('#VALUE');
  const shape = isRange(left) ? left : right;
  return { rows: shape.rows, cols: shape.cols, values: shape.values.map((_, i) =>
    operation(isRange(left) ? left.values[i] : left, isRange(right) ? right.values[i] : right)) };
}

function createParser(tokens, resolve) {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expect = (type) => {
    const token = next();
    if (!token || token.type !== type) throw new FormulaError('#ERR');
    return token;
  };

  const readReference = (start) => {
    if (peek()?.type === ':') {
      next(); // ':'
      const endToken = next();
      if (endToken?.type !== 'ref') throw new FormulaError('#REF');
      const end = endToken.ref;
      if (start.sheet && end.sheet && start.sheet.toLowerCase() !== end.sheet.toLowerCase()) {
        throw new FormulaError('#REF');
      }
      const sheet = start.sheet || end.sheet;
      const values = [];
      const r1 = Math.min(start.r, end.r); const r2 = Math.max(start.r, end.r);
      const c1 = Math.min(start.c, end.c); const c2 = Math.max(start.c, end.c);
      if ((r2 - r1 + 1) * (c2 - c1 + 1) > 10000) throw new FormulaError('#REF');
      for (let r = r1; r <= r2; r += 1) {
        for (let c = c1; c <= c2; c += 1) {
          values.push(resolve(sheet ? { r, c, sheet } : { r, c }));
        }
      }
      return { values, rows: r2 - r1 + 1, cols: c2 - c1 + 1 };
    }
    const value = resolve(start);
    if (isFormulaError(value)) throw new FormulaError(value);
    return value;
  };
  const argValues = () => flatten(parseExpr());

  const callFunction = (name) => {
    expect('(');

    if (name === 'IF' || name === 'ISNUMBER') {
      // Split at this call's commas before evaluating anything. Resolving an
      // unused branch can raise division errors or create a false cell cycle.
      const args = [];
      let start = pos;
      let depth = 0;
      while (pos < tokens.length) {
        const token = peek();
        if (token.type === '(') depth += 1;
        else if (token.type === ')') {
          if (depth === 0) break;
          depth -= 1;
        } else if (token.type === ',' && depth === 0) {
          args.push(tokens.slice(start, pos));
          start = pos + 1;
        }
        next();
      }
      args.push(tokens.slice(start, pos));
      expect(')');
      if (name === 'ISNUMBER') {
        if (args.length !== 1 || !args[0].length) throw new FormulaError('#ERR');
        try {
          const value = createParser(args[0], resolve)();
          return typeof value === 'number' && Number.isFinite(value);
        } catch (error) {
          // Excel's type predicate returns false for an error-valued input.
          if (error instanceof FormulaError && error.code !== '#ERR') return false;
          throw error;
        }
      }
      if (args.length < 2 || args.length > 3 || !args[0].length) throw new FormulaError('#ERR');
      const condition = createParser(args[0], resolve)();
      if (isRange(condition)) throw new FormulaError('#VALUE');
      const truthy = typeof condition === 'string' && /^(TRUE|FALSE)$/i.test(condition)
        ? condition.toUpperCase() === 'TRUE' : toNumber(condition) !== 0;
      const branch = args[truthy ? 1 : 2];
      if (!branch) return false;
      return branch.length ? createParser(branch, resolve)() : 0;
    }

    if (['AND', 'OR', 'NOT', 'INT'].includes(name)) {
      const args = [];
      const readArgument = () => {
        const start = pos;
        const value = parseExpr();
        const referenced = (pos === start + 1 && tokens[start].type === 'ref') || isRange(value);
        return { value, referenced };
      };
      if (peek()?.type !== ')') {
        args.push(readArgument());
        while (peek()?.type === ',') { next(); args.push(readArgument()); }
      }
      expect(')');
      const logical = value => typeof value === 'string' && /^(TRUE|FALSE)$/i.test(value)
        ? value.toUpperCase() === 'TRUE' : toNumber(value) !== 0;
      if (name === 'NOT' || name === 'INT') {
        if (args.length !== 1 || isRange(args[0].value)) throw new FormulaError('#VALUE');
        return name === 'NOT' ? !logical(args[0].value) : Math.floor(toNumber(args[0].value));
      }
      const values = [];
      for (const { value, referenced } of args) {
        for (const item of flatten(value)) {
          if (isFormulaError(item)) throw new FormulaError(item);
          // Logical aggregates ignore text/blank cells in references, but
          // coerce literal arguments. They must not hide error-valued cells.
          if (referenced && typeof item !== 'number' && typeof item !== 'boolean') continue;
          values.push(logical(item));
        }
      }
      if (!values.length) throw new FormulaError('#VALUE');
      return name === 'AND' ? values.every(Boolean) : values.some(Boolean);
    }

    if (name === 'LEFT' || name === 'SUMPRODUCT') {
      const args = [];
      if (peek()?.type !== ')') {
        args.push(parseExpr());
        while (peek()?.type === ',') { next(); args.push(parseExpr()); }
      }
      expect(')');
      if (name === 'LEFT') {
        if (args.length < 1 || args.length > 2 || isRange(args[1])) throw new FormulaError('#VALUE');
        const length = Math.trunc(toNumber(args[1] ?? 1));
        if (length < 0) throw new FormulaError('#VALUE');
        return combineValues(args[0], length, (value, n) => {
          if (isFormulaError(value)) throw new FormulaError(value);
          return String(value ?? '').slice(0, n);
        });
      }
      if (!args.length) throw new FormulaError('#ERR');
      const shape = args.find(isRange);
      if (shape && args.some(arg => !isRange(arg) || arg.rows !== shape.rows || arg.cols !== shape.cols)) {
        throw new FormulaError('#VALUE');
      }
      // SUMPRODUCT ignores nonnumeric entries in direct range arguments;
      // arithmetic within an argument has already applied Excel coercion.
      const number = value => {
        if (isFormulaError(value)) throw new FormulaError(value);
        return typeof value === 'number' ? value : 0;
      };
      let product = args[0];
      if (args.length === 1) return flatten(product).reduce((sum, value) => sum + number(value), 0);
      for (let i = 1; i < args.length; i += 1) product = combineValues(product, args[i], (a, b) => number(a) * number(b));
      return flatten(product).reduce((sum, value) => sum + number(value), 0);
    }

    if (['SUMIF', 'SUMIFS', 'COUNTIF', 'COUNTIFS'].includes(name)) {
      const args = [];
      const readArgument = () => {
        // Criteria cells may contain text, unlike numeric arithmetic operands.
        if (peek()?.type === 'ref' && [',', ')'].includes(tokens[pos + 1]?.type)) {
          return [resolve(next().ref)];
        }
        return argValues();
      };
      if (peek()?.type !== ')') {
        args.push(readArgument());
        while (peek()?.type === ',') { next(); args.push(readArgument()); }
      }
      expect(')');
      const sum = name.startsWith('SUM');
      const multiple = name.endsWith('IFS');
      if ((!multiple && (args.length < 2 || args.length > (sum ? 3 : 2))) ||
          (multiple && (args.length < (sum ? 3 : 2) || args.length % 2 !== (sum ? 1 : 0)))) {
        throw new FormulaError('#ERR');
      }
      const pairs = multiple ? args.slice(sum ? 1 : 0) : args.slice(0, 2);
      const values = sum ? (multiple ? args[0] : args[2] || args[0]) : pairs[0];
      for (let i = 0; i < pairs.length; i += 2) {
        if (pairs[i].length !== values.length || pairs[i + 1].length !== 1) throw new FormulaError('#VALUE');
        if (isFormulaError(pairs[i + 1][0])) throw new FormulaError(pairs[i + 1][0]);
      }
      return values.reduce((total, value, index) => {
        for (let i = 0; i < pairs.length; i += 2) {
          if (!matchesCriteria(pairs[i][index], pairs[i + 1][0])) return total;
        }
        if (!sum) return total + 1;
        if (isFormulaError(value)) throw new FormulaError(value);
        return total + (typeof value === 'number' ? value : 0);
      }, 0);
    }

    // IFERROR must evaluate its fallback lazily. Parsing every argument into
    // `values` first would let an expected divide-by-zero escape before the
    // function has a chance to replace it. If the first expression fails,
    // resynchronize at its top-level comma so nested calls still work.
    if (name === 'IFERROR') {
      const parseProtectedArgument = () => {
        const start = pos;
        try {
          return { value: parseExpr(), error: null };
        } catch (error) {
          pos = start;
          let depth = 0;
          while (pos < tokens.length) {
            const token = tokens[pos];
            if (token.type === '(') depth += 1;
            else if (token.type === ')') {
              if (depth === 0) break;
              depth -= 1;
            } else if (token.type === ',' && depth === 0) {
              break;
            }
            pos += 1;
          }
          return { value: null, error };
        }
      };

      const primary = parseProtectedArgument();
      expect(',');
      const fallback = parseProtectedArgument();
      expect(')');
      if (primary.error && fallback.error) throw fallback.error;
      return primary.error ? fallback.value : primary.value;
    }

    const values = [];
    if (peek() && peek().type !== ')') {
      values.push(...argValues());
      while (peek()?.type === ',') {
        next();
        values.push(...argValues());
      }
    }
    expect(')');

    const numbers = values
      .filter(value => value !== '' && value !== null && value !== undefined && typeof value !== 'boolean')
      .map((value) => {
        if (isFormulaError(value)) throw new FormulaError(value);
        if (typeof value === 'number') return value;
        const numeric = Number(String(value).replace(/,/g, ''));
        return Number.isFinite(numeric) ? numeric : null;
      })
      .filter(value => value !== null);

    switch (name) {
      case 'SUM': return numbers.reduce((total, value) => total + value, 0);
      case 'AVERAGE':
      case 'AVG':
        if (!numbers.length) throw new FormulaError('#DIV/0');
        return numbers.reduce((total, value) => total + value, 0) / numbers.length;
      case 'MIN': return numbers.length ? Math.min(...numbers) : 0;
      case 'MAX': return numbers.length ? Math.max(...numbers) : 0;
      case 'COUNT': return numbers.length;
      case 'COUNTA':
        return values.filter(value => value !== '' && value !== null && value !== undefined).length;
      case 'ROUND': {
        const [value = 0, digits = 0] = values.map(toNumber);
        const factor = 10 ** Math.trunc(digits);
        return Math.round(value * factor) / factor;
      }
      case 'ABS': return Math.abs(toNumber(values[0] ?? 0));
      case 'SQRT': {
        const value = toNumber(values[0] ?? 0);
        if (value < 0) throw new FormulaError('#VALUE');
        return Math.sqrt(value);
      }
      default: throw new FormulaError('#ERR');
    }
  };

  const parsePrimary = () => {
    const token = next();
    if (!token) throw new FormulaError('#ERR');
    if (token.type === 'number') return token.value;
    if (token.type === 'boolean') return token.value;
    if (token.type === 'string') return token.value;
    if (token.type === 'error') throw new FormulaError(token.value);
    if (token.type === 'ref') return readReference(token.ref);
    if (token.type === 'function') return callFunction(token.name);
    if (token.type === '(') {
      const value = parseExpr();
      expect(')');
      return value;
    }
    throw new FormulaError('#ERR');
  };

  const parseUnary = () => {
    let sign = 1;
    let hasSign = false;
    while (peek()?.type === '-' || peek()?.type === '+') {
      hasSign = true;
      if (next().type === '-') sign = -sign;
    }
    const value = parseFactor0();
    return hasSign ? combineValues(value, sign, (a, b) => toNumber(a) * b) : value;
  };

  const parseFactor0 = () => {
    const base = parsePrimary();
    if (peek()?.type === '^') {
      next();
      return combineValues(base, parseUnary(), (a, b) => toNumber(a) ** toNumber(b));
    }
    return base;
  };

  const parseTerm = () => {
    let value = parseUnary();
    while (peek()?.type === '*' || peek()?.type === '/') {
      const op = next().type;
      const rhs = parseUnary();
      value = combineValues(value, rhs, (a, b) => {
        const left = toNumber(a); const right = toNumber(b);
        if (op === '/' && right === 0) throw new FormulaError('#DIV/0');
        return op === '/' ? left / right : left * right;
      });
    }
    return value;
  };

  const parseAdditive = () => {
    let value = parseTerm();
    while (peek()?.type === '+' || peek()?.type === '-') {
      const op = next().type;
      const rhs = parseTerm();
      value = combineValues(value, rhs, (a, b) => op === '+' ? toNumber(a) + toNumber(b) : toNumber(a) - toNumber(b));
    }
    return value;
  };

  const parseConcatenation = () => {
    let value = parseAdditive();
    while (peek()?.type === '&') {
      next();
      value = combineValues(value, parseAdditive(), (a, b) => {
        const text = item => {
          if (isFormulaError(item)) throw new FormulaError(item);
          return typeof item === 'boolean' ? String(item).toUpperCase() : String(item ?? '');
        };
        return text(a) + text(b);
      });
    }
    return value;
  };

  const parseExpr = () => {
    let value = parseConcatenation();
    while (peek()?.type === 'comparison') {
      const { op } = next();
      value = combineValues(value, parseConcatenation(), (a, b) => {
        if (isFormulaError(a)) throw new FormulaError(a);
        if (isFormulaError(b)) throw new FormulaError(b);
        const left = typeof a === 'string' ? a.toLowerCase() : a ?? 0;
        const right = typeof b === 'string' ? b.toLowerCase() : b ?? 0;
        switch (op) {
          case '=': return left === right;
          case '<>': return left !== right;
          case '<': return left < right;
          case '>': return left > right;
          case '<=': return left <= right;
          default: return left >= right;
        }
      });
    }
    return value;
  };

  return () => {
    const value = parseExpr();
    if (pos !== tokens.length) throw new FormulaError('#ERR');
    return value;
  };
}

/**
 * Evaluate formula source (without the leading '='). `resolve({r,c})` must
 * return the referenced cell's value (computed, for formula cells).
 * Returns a number, or an error string like '#DIV/0'.
 */
export function evaluateFormula(src, resolve) {
  try {
    const parse = createParser(tokenize(src), resolve);
    const value = parse();
    if (isRange(value)) return '#VALUE';
    if (typeof value === 'number' && !Number.isFinite(value)) return '#DIV/0';
    return typeof value === 'number' ? Math.round(value * 1e10) / 1e10 : value;
  } catch (error) {
    return error instanceof FormulaError ? error.code : '#ERR';
  }
}

/**
 * Recompute every formula cell in a sheet (rows: cell[][] where formula
 * cells carry `f`). Returns a new rows array when any computed value
 * changed, otherwise the original reference. Handles chained references
 * and flags cycles as '#CYCLE'.
 */
export function recomputeSheetRows(rows) {
  return recomputeWorkbookSheets([{ name: '', rows }])[0].rows;
}

/** Recompute with workbook-wide references, memoization, and cycle detection. */
export function recomputeWorkbookSheets(sheets) {
  const memo = new Map();
  const inProgress = new Set();
  const names = new Map(sheets.map((sheet, index) => [String(sheet.name || '').toLowerCase(), index]));
  const resolve = (sheetIndex, { r, c, sheet }) => {
    const index = sheet == null ? sheetIndex : names.get(sheet.toLowerCase());
    if (index == null) return '#REF';
    const key = `${index}:${r},${c}`;
    if (memo.has(key)) return memo.get(key);
    const cell = sheets[index].rows[r]?.[c];
    if (!cell) return '';
    if (typeof cell.f === 'string' && cell.f.startsWith('=')) {
      if (inProgress.has(key)) return '#CYCLE';
      inProgress.add(key);
      const value = evaluateFormula(cell.f.slice(1), ref => resolve(index, ref));
      inProgress.delete(key);
      memo.set(key, value);
      return value;
    }
    memo.set(key, cell.v ?? '');
    return cell.v ?? '';
  };

  let changed = false;
  const nextSheets = sheets.map((sheet, index) => {
    let sheetChanged = false;
    const rows = sheet.rows.map((row, r) => {
      let rowChanged = false;
      const nextRow = row.map((cell, c) => {
        if (typeof cell?.f !== 'string' || !cell.f.startsWith('=')) return cell;
        const value = resolve(index, { r, c });
        if (value === cell.v) return cell;
        rowChanged = true;
        return { ...cell, v: value };
      });
      if (rowChanged) sheetChanged = true;
      return rowChanged ? nextRow : row;
    });
    const charts = refreshSpreadsheetCharts(sheet.charts, rows);
    const chartsChanged = charts !== sheet.charts;
    if (sheetChanged || chartsChanged) changed = true;
    return sheetChanged || chartsChanged ? { ...sheet, rows, ...(chartsChanged ? { charts } : {}) } : sheet;
  });
  return changed ? nextSheets : sheets;
}
